import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { PageShell } from '../../components/layout/page/PageShell';
import { CalendarDayView } from '../../components/calendars/CalendarDayView';
import { CalendarEventModal } from '../../components/calendars/CalendarEventModal';
import { CalendarMonthView } from '../../components/calendars/CalendarMonthView';
import { CalendarViewSelector } from '../../components/calendars/CalendarViewSelector';
import { CalendarViewMode } from '../../components/calendars/CalendarViewMode';
import { CalendarWeekView } from '../../components/calendars/CalendarWeekView';
import { CalendarWorkScheduleModal } from '../../components/calendars/CalendarWorkScheduleModal';
import { CalendarYearView } from '../../components/calendars/CalendarYearView';
import { NewCalendarModal } from '../../components/calendars/NewCalendarModal';
import { CalendarTasksModal } from '../../components/calendars/CalendarTasksModal';
import { TasksCalendarVisibilityFilters } from '../../components/calendars/TasksCalendarVisibilityFilters';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  formatDayTitle,
  formatMonthYear,
  formatWeekTitle,
  formatYearTitle,
  getCalendarColor,
  getMonthGrid,
  formatInputDate,
  startOfDay,
  startOfMonth,
  startOfWeek,
  TASKS_CALENDAR_ID,
  TASKS_CALENDAR_COLOR,
  getTasksCalendarEventColor,
  isTasksCalendarStatusVisible,
  isTasksCalendarTaskKind,
  normalizeTaskCalendarIds,
  TASKS_CALENDAR_LABEL,
} from '../../components/calendars/calendarUtils';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  CalendarPlottedEvent,
  CloneEnterpriseCalendarPayload,
  EnterpriseCalendar,
  EnterpriseCalendarEventUpsertPayload,
  EnterpriseCalendarSummary,
  CalendarWorkDay,
  cloneEnterpriseCalendar,
  createCalendarEvent,
  deleteCalendarEvent,
  getEffectiveEnterpriseCalendar,
  getEnterpriseCalendar,
  listEnterpriseCalendars,
  updateCalendarEvent,
  updateCalendarWorkSchedule,
} from '../../services/enterpriseCalendarsApi';
import {
  cancelTasksCalendarTask,
  createTasksCalendarTask,
  listTasksCalendarEvents,
  listTasksCalendarUsers,
  TasksCalendarEvent,
  TasksCalendarUser,
  updateTasksCalendarTask,
} from '../../services/tasksCalendarApi';

export function CalendarsPage() {
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('operations.calendars');
  const loadErrorShown = useRef(false);

  const [calendars, setCalendars] = useState<EnterpriseCalendarSummary[]>([]);
  const [activeCalendarId, setActiveCalendarId] = useState<string | null>(TASKS_CALENDAR_ID);
  const [visibleCalendarIds, setVisibleCalendarIds] = useState<Set<string>>(new Set());
  const [eventsByCalendar, setEventsByCalendar] = useState<Record<string, EnterpriseCalendar>>({});
  const [currentDate, setCurrentDate] = useState(() => startOfDay(new Date()));
  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [loading, setLoading] = useState(true);

  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [newCalendarModalOpen, setNewCalendarModalOpen] = useState(false);
  const [workScheduleModalOpen, setWorkScheduleModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | undefined>();
  const [selectedHour, setSelectedHour] = useState<number | undefined>();
  const [editingEvent, setEditingEvent] = useState<CalendarPlottedEvent | null>(null);

  const [tasksCalendarUsers, setTasksCalendarUsers] = useState<TasksCalendarUser[]>([]);
  const [tasksCalendarVisible, setTasksCalendarVisible] = useState(true);
  const [visibleTaskUserIds, setVisibleTaskUserIds] = useState<Set<string>>(new Set());
  const [taskEvents, setTaskEvents] = useState<TasksCalendarEvent[]>([]);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [showCancelledTasks, setShowCancelledTasks] = useState(false);
  const [tasksModalOpen, setTasksModalOpen] = useState(false);
  const [tasksModalMode, setTasksModalMode] = useState<'create' | 'view'>('create');
  const [viewingTaskEvent, setViewingTaskEvent] = useState<CalendarPlottedEvent | null>(null);
  const [defaultTaskOwnerUserId, setDefaultTaskOwnerUserId] = useState<string | undefined>();

  const isTasksCalendarActive = activeCalendarId === TASKS_CALENDAR_ID;

  const activeCalendar = useMemo(
    () => calendars.find((calendar) => calendar.id === activeCalendarId) ?? null,
    [calendars, activeCalendarId],
  );

  const periodTitle = useMemo(() => {
    switch (viewMode) {
      case 'day':
        return formatDayTitle(currentDate);
      case 'week':
        return formatWeekTitle(currentDate);
      case 'year':
        return formatYearTitle(currentDate);
      default:
        return formatMonthYear(currentDate);
    }
  }, [currentDate, viewMode]);

  function goToToday(): void {
    setCurrentDate(startOfDay(new Date()));
  }

  function goToPreviousPeriod(): void {
    setCurrentDate((date) => {
      switch (viewMode) {
        case 'day':
          return addDays(date, -1);
        case 'week':
          return addWeeks(date, -1);
        case 'year':
          return addYears(date, -1);
        default:
          return startOfMonth(addMonths(date, -1));
      }
    });
  }

  function goToNextPeriod(): void {
    setCurrentDate((date) => {
      switch (viewMode) {
        case 'day':
          return addDays(date, 1);
        case 'week':
          return addWeeks(date, 1);
        case 'year':
          return addYears(date, 1);
        default:
          return startOfMonth(addMonths(date, 1));
      }
    });
  }

  function openCreateEvent(day: Date, hour?: number): void {
    if (activeCalendarId === TASKS_CALENDAR_ID) {
      openCreateTask(day);
      return;
    }

    if (!canWrite) return;
    setEditingEvent(null);
    setSelectedDay(day);
    setSelectedHour(hour);
    setEventModalOpen(true);
  }

  function openCreateTask(day: Date, ownerUserId?: string): void {
    setTasksModalMode('create');
    setViewingTaskEvent(null);
    setSelectedDay(day);
    setDefaultTaskOwnerUserId(ownerUserId);
    setTasksModalOpen(true);
  }

  function openViewTask(event: CalendarPlottedEvent): void {
    if (event.eventSource !== 'tasks') {
      openEditEvent(event);
      return;
    }

    setTasksModalMode('view');
    setViewingTaskEvent(event);
    setTasksModalOpen(true);
  }

  function openEditEvent(event: CalendarPlottedEvent): void {
    if (event.eventSource === 'tasks') {
      openViewTask(event);
      return;
    }

    if (!canWrite) return;
    setEditingEvent(event);
    setSelectedDay(startOfDay(new Date(event.eventDate)));
    setSelectedHour(undefined);
    setEventModalOpen(true);
  }

  function closeEventModal(): void {
    setEventModalOpen(false);
    setEditingEvent(null);
    setSelectedHour(undefined);
  }

  const activeCalendarDetails = activeCalendarId ? eventsByCalendar[activeCalendarId] ?? null : null;

  const visibleDateRange = useMemo(() => {
    switch (viewMode) {
      case 'day':
        return { from: formatInputDate(currentDate), to: formatInputDate(currentDate) };
      case 'week': {
        const start = startOfWeek(currentDate);
        const end = addDays(start, 6);
        return { from: formatInputDate(start), to: formatInputDate(end) };
      }
      case 'year': {
        const start = new Date(currentDate.getFullYear(), 0, 1);
        const end = new Date(currentDate.getFullYear(), 11, 31);
        return { from: formatInputDate(start), to: formatInputDate(end) };
      }
      default: {
        const grid = getMonthGrid(startOfMonth(currentDate));
        return { from: formatInputDate(grid[0]), to: formatInputDate(grid[grid.length - 1]) };
      }
    }
  }, [currentDate, viewMode]);

  const plottedEvents = useMemo(() => {
    const result: CalendarPlottedEvent[] = [];

    taskEvents.forEach((task) => {
        if (!isTasksCalendarTaskKind(task.kind)) {
          return;
        }

        if (!isTasksCalendarStatusVisible(task.status, showCompletedTasks, showCancelledTasks)) {
          return;
        }

        const startDate = task.plannedStartDate ?? task.plannedEndDate;
        if (!startDate) return;

        const assignedCalendarIds = normalizeTaskCalendarIds(task.calendarIds);
        const showOnTasksAgenda = assignedCalendarIds.includes(TASKS_CALENDAR_ID) && tasksCalendarVisible;
        const visibleCompanyCalendar = calendars.find((calendar) => (
          assignedCalendarIds.includes(calendar.id) && visibleCalendarIds.has(calendar.id)
        ));

        if (!showOnTasksAgenda && !visibleCompanyCalendar) return;

        const companyIndex = visibleCompanyCalendar
          ? calendars.findIndex((calendar) => calendar.id === visibleCompanyCalendar.id)
          : -1;
        const placement = showOnTasksAgenda
          ? {
              calendarId: TASKS_CALENDAR_ID,
              calendarName: TASKS_CALENDAR_LABEL,
              color: getTasksCalendarEventColor(task.status),
            }
          : {
              calendarId: visibleCompanyCalendar!.id,
              calendarName: visibleCompanyCalendar!.name,
              color: getCalendarColor(companyIndex),
            };

        result.push({
          id: task.id,
          name: task.ownerUserName && !tasksCalendarUsers.find((user) => user.isSelf && visibleTaskUserIds.has(user.userId))
            ? `${task.title} (${task.ownerUserName})`
            : task.title,
          description: task.description,
          type: 'Event',
          eventDate: startDate,
          isAllDay: true,
          recurrenceType: 'None',
          isRecurringYearly: false,
          excludeFromSla: false,
          scope: 'tasks',
          createdAtUtc: startDate,
          updatedAtUtc: startDate,
          calendarId: placement.calendarId,
          calendarName: placement.calendarName,
          color: placement.color,
          eventSource: 'tasks',
          taskEndDate: task.plannedEndDate ?? task.plannedStartDate,
          workItemId: task.id,
          ownerUserId: task.ownerUserId,
          ownerUserName: task.ownerUserName,
          taskStatus: task.status,
          taskPriority: task.priority,
          taskTitle: task.title,
          taskCalendarIds: assignedCalendarIds,
          taskAreas: task.areas ?? [],
          metaTitle: task.metaTitle,
          marcoTitle: task.marcoTitle,
        });
      });

    calendars.forEach((calendar, index) => {
      if (!visibleCalendarIds.has(calendar.id)) return;

      const details = eventsByCalendar[calendar.id];
      if (!details) return;

      const color = getCalendarColor(index);
      details.events.forEach((event) => {
        result.push({
          ...event,
          calendarId: calendar.id,
          calendarName: calendar.name,
          color,
        });
      });
    });

    return result;
  }, [
    calendars,
    visibleCalendarIds,
    eventsByCalendar,
    tasksCalendarVisible,
    taskEvents,
    tasksCalendarUsers,
    visibleTaskUserIds,
    showCompletedTasks,
    showCancelledTasks,
  ]);

  const refreshCalendarEvents = useCallback(
    async (calendarId: string, isGlobal: boolean): Promise<void> => {
      const details = isGlobal
        ? await getEnterpriseCalendar(calendarId)
        : await getEffectiveEnterpriseCalendar(calendarId);
      setEventsByCalendar((current) => ({ ...current, [calendarId]: details }));
    },
    [],
  );

  const loadCalendars = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const result = await listEnterpriseCalendars({ page: 1, pageSize: 100 });
      const items = result.items;
      setCalendars(items);

      const global = items.find((calendar) => calendar.isGlobal) ?? items[0];
      if (!global) {
        return;
      }

      setActiveCalendarId((current) => current ?? global.id);
      setVisibleCalendarIds((current) => {
        if (current.size > 0) {
          return current;
        }

        return new Set(items.map((calendar) => calendar.id));
      });

      await Promise.all(items.map((calendar) => refreshCalendarEvents(calendar.id, calendar.isGlobal)));
      loadErrorShown.current = false;
    } catch {
      if (!loadErrorShown.current) {
        loadErrorShown.current = true;
        toast.error('Falha ao carregar calendários.');
      }
    } finally {
      setLoading(false);
    }
  }, [refreshCalendarEvents]);

  useEffect(() => {
    void loadCalendars();
  }, [loadCalendars]);

  const loadTasksCalendarUsers = useCallback(async (): Promise<void> => {
    try {
      const users = await listTasksCalendarUsers();
      setTasksCalendarUsers(users);
      setVisibleTaskUserIds((current) => {
        if (current.size > 0) {
          return current;
        }

        return new Set(users.map((user) => user.userId));
      });
    } catch {
      toast.error('Falha ao carregar agenda de tarefas.');
    }
  }, []);

  const loadTaskEvents = useCallback(async (): Promise<void> => {
    if (visibleTaskUserIds.size === 0) {
      setTaskEvents([]);
      return;
    }

    try {
      const events = await listTasksCalendarEvents({
        from: visibleDateRange.from,
        to: visibleDateRange.to,
        userIds: Array.from(visibleTaskUserIds),
      });
      setTaskEvents(events);
    } catch {
      toast.error('Falha ao carregar tarefas do calendário.');
    }
  }, [visibleDateRange.from, visibleDateRange.to, visibleTaskUserIds]);

  useEffect(() => {
    void loadTasksCalendarUsers();
  }, [loadTasksCalendarUsers]);

  useEffect(() => {
    void loadTaskEvents();
  }, [loadTaskEvents]);

  function toggleTaskUserVisibility(userId: string): void {
    setVisibleTaskUserIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }

  async function handleCreateTask(payload: Parameters<typeof createTasksCalendarTask>[0]): Promise<void> {
    if (!payload.ownerUserId) {
      toast.error('Selecione o responsável pela tarefa.');
      return;
    }

    try {
      await createTasksCalendarTask(payload);
      await loadTaskEvents();
      toast.success('Tarefa criada.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao criar tarefa.'));
      throw error;
    }
  }

  async function handleUpdateTask(
    taskId: string,
    payload: Parameters<typeof updateTasksCalendarTask>[1],
  ): Promise<void> {
    try {
      await updateTasksCalendarTask(taskId, payload);
      await loadTaskEvents();
      toast.success('Tarefa atualizada.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao atualizar tarefa.'));
      throw error;
    }
  }

  async function handleCancelTask(taskId: string, cancellationReason: string): Promise<void> {
    try {
      await cancelTasksCalendarTask(taskId, cancellationReason);
      await loadTaskEvents();
      toast.success('Tarefa cancelada.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao cancelar tarefa.'));
      throw error;
    }
  }

  function toggleCalendarVisibility(calendarId: string): void {
    setVisibleCalendarIds((current) => {
      const next = new Set(current);
      if (next.has(calendarId)) {
        next.delete(calendarId);
      } else {
        next.add(calendarId);
      }
      return next;
    });
  }

  async function handleSaveEvent(payload: EnterpriseCalendarEventUpsertPayload): Promise<void> {
    if (editingEvent) {
      try {
        await updateCalendarEvent(editingEvent.calendarId, editingEvent.id, payload);
        const source = calendars.find((calendar) => calendar.id === editingEvent.calendarId);
        await refreshCalendarEvents(editingEvent.calendarId, source?.isGlobal ?? false);
        toast.success('Evento atualizado.');
      } catch {
        toast.error('Falha ao atualizar evento.');
        throw new Error('update-event-failed');
      }
      return;
    }

    if (!activeCalendarId) return;

    try {
      await createCalendarEvent(activeCalendarId, payload);
      await refreshCalendarEvents(activeCalendarId, activeCalendar?.isGlobal ?? false);
      toast.success('Evento criado.');
    } catch {
      toast.error('Falha ao criar evento.');
      throw new Error('create-event-failed');
    }
  }

  async function handleDeleteEvent(): Promise<void> {
    if (!editingEvent) return;

    try {
      await deleteCalendarEvent(editingEvent.calendarId, editingEvent.id);
      const source = calendars.find((calendar) => calendar.id === editingEvent.calendarId);
      await refreshCalendarEvents(editingEvent.calendarId, source?.isGlobal ?? false);
      toast.success('Evento excluído.');
    } catch {
      toast.error('Falha ao excluir evento.');
      throw new Error('delete-event-failed');
    }
  }

  async function handleCreateCalendar(payload: CloneEnterpriseCalendarPayload): Promise<void> {
    try {
      const created = await cloneEnterpriseCalendar({
        ...payload,
        sourceCalendarId:
          payload.sourceCalendarId && payload.sourceCalendarId !== TASKS_CALENDAR_ID
            ? payload.sourceCalendarId
            : undefined,
      });
      const result = await listEnterpriseCalendars({ page: 1, pageSize: 100 });
      setCalendars(result.items);
      setActiveCalendarId(created.id);
      setVisibleCalendarIds((current) => new Set([...Array.from(current), created.id]));
      await refreshCalendarEvents(created.id, created.isGlobal);
      toast.success('Calendário criado.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao criar calendário.'));
      throw new Error('create-calendar-failed');
    }
  }

  async function handleSaveWorkSchedule(workDays: CalendarWorkDay[]): Promise<void> {
    if (!activeCalendarId) return;

    try {
      const updated = await updateCalendarWorkSchedule(activeCalendarId, { workDaySchedules: workDays });
      setEventsByCalendar((current) => ({ ...current, [activeCalendarId]: updated }));
      toast.success('Horários atualizados.');
    } catch {
      toast.error('Falha ao salvar horários.');
      throw new Error('save-schedule-failed');
    }
  }

  async function openWorkScheduleModal(): Promise<void> {
    if (!activeCalendarId) return;

    try {
      const details = await getEnterpriseCalendar(activeCalendarId);
      setEventsByCalendar((current) => ({ ...current, [activeCalendarId]: details }));
      setWorkScheduleModalOpen(true);
    } catch {
      toast.error('Falha ao carregar horários do calendário.');
    }
  }

  return (
    <PageShell className="!p-0">
      <div className="flex h-[calc(100vh-4rem)] min-h-[640px] flex-col bg-slate-100 lg:flex-row">
        <aside className="w-full shrink-0 border-b border-slate-200 bg-white p-4 lg:w-72 lg:border-b-0 lg:border-r">
          <div className="space-y-2">
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-full border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium shadow-sm hover:bg-amber-100"
              onClick={() => openCreateTask(new Date())}
            >
              <PlusIcon className="h-5 w-5 text-amber-600" />
              Nova tarefa
            </button>
            {canWrite ? (
              <>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-full border border-slate-200 px-4 py-3 text-sm font-medium shadow-sm hover:bg-slate-50"
                  onClick={() => {
                    if (activeCalendarId === TASKS_CALENDAR_ID && calendars[0]) {
                      setActiveCalendarId(calendars[0].id);
                    }
                    openCreateEvent(new Date());
                  }}
                >
                  <PlusIcon className="h-5 w-5 text-primary-600" />
                  Novo evento
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-full border border-slate-200 px-4 py-3 text-sm font-medium shadow-sm hover:bg-slate-50"
                  onClick={() => setNewCalendarModalOpen(true)}
                >
                  <PlusIcon className="h-5 w-5 text-emerald-600" />
                  Novo calendário
                </button>
              </>
            ) : null}
          </div>

          <div className="mt-6">
            <p className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Minha agenda
            </p>
            <div className="mt-2 space-y-1">
              <div
                className={`rounded-xl px-2 py-2 ${
                  isTasksCalendarActive ? 'bg-amber-50 ring-1 ring-amber-200' : 'hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={tasksCalendarVisible}
                    onChange={() => setTasksCalendarVisible((current) => !current)}
                    className="rounded border-slate-300"
                  />
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
                    onClick={() => setActiveCalendarId(TASKS_CALENDAR_ID)}
                  >
                    <span
                      className="h-3 w-3 shrink-0 rounded-sm ring-1 ring-amber-500/40"
                      style={{ backgroundColor: TASKS_CALENDAR_COLOR }}
                    />
                    <span className="truncate font-semibold text-slate-800">Tarefas</span>
                  </button>
                </div>

                {tasksCalendarVisible ? (
                  <div className="ml-6 mt-2 space-y-1 border-l border-amber-100 pl-3">
                    {tasksCalendarUsers.map((user) => (
                      <label key={user.userId} className="flex items-center gap-2 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={visibleTaskUserIds.has(user.userId)}
                          onChange={() => toggleTaskUserVisibility(user.userId)}
                          className="rounded border-slate-300"
                        />
                        <span className="truncate">
                          {user.name}
                          {user.isSelf ? ' (eu)' : ''}
                        </span>
                      </label>
                    ))}
                    <TasksCalendarVisibilityFilters
                      className="space-y-1 border-t border-amber-100 pt-2"
                      showCompleted={showCompletedTasks}
                      showCancelled={showCancelledTasks}
                      onShowCompletedChange={setShowCompletedTasks}
                      onShowCancelledChange={setShowCancelledTasks}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <p className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Calendários da empresa
            </p>
            <div className="mt-2 space-y-1">
              {loading ? (
                <p className="px-2 text-sm text-slate-500">Carregando...</p>
              ) : (
                calendars.map((calendar, index) => (
                  <div
                    key={calendar.id}
                    className={`flex items-center gap-2 rounded-xl px-2 py-2 ${
                      activeCalendarId === calendar.id ? 'bg-primary-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={visibleCalendarIds.has(calendar.id)}
                      onChange={() => toggleCalendarVisibility(calendar.id)}
                      className="rounded border-slate-300"
                    />
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
                      onClick={() => setActiveCalendarId(calendar.id)}
                    >
                      <span
                        className="h-3 w-3 shrink-0 rounded-sm"
                        style={{ backgroundColor: getCalendarColor(index) }}
                      />
                      <span className="truncate font-medium text-slate-800">{calendar.name}</span>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>

        <section className="flex min-h-0 flex-1 flex-col p-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50"
                onClick={goToToday}
              >
                Hoje
              </button>
              <button
                type="button"
                className="rounded-xl border border-slate-300 bg-white p-2 hover:bg-slate-50"
                onClick={goToPreviousPeriod}
                aria-label="Período anterior"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                className="rounded-xl border border-slate-300 bg-white p-2 hover:bg-slate-50"
                onClick={goToNextPeriod}
                aria-label="Próximo período"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-semibold capitalize text-slate-900">{periodTitle}</h1>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <CalendarViewSelector value={viewMode} onChange={setViewMode} />

              {canWrite && activeCalendar && !isTasksCalendarActive ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
                  onClick={() => void openWorkScheduleModal()}
                >
                  <Cog6ToothIcon className="h-5 w-5" />
                  Horários da semana
                </button>
              ) : null}
            </div>
          </div>

          <div className="min-h-0 flex-1">
            {viewMode === 'day' ? (
              <CalendarDayView
                day={currentDate}
                events={plottedEvents}
                onDayClick={openCreateEvent}
                onEventClick={openViewTask}
              />
            ) : null}
            {viewMode === 'week' ? (
              <CalendarWeekView
                anchorDate={currentDate}
                events={plottedEvents}
                onDayClick={(day) => openCreateEvent(day)}
                onEventClick={openViewTask}
              />
            ) : null}
            {viewMode === 'month' ? (
              <CalendarMonthView
                month={startOfMonth(currentDate)}
                events={plottedEvents}
                onDayClick={(day) => openCreateEvent(day)}
                onEventClick={openViewTask}
              />
            ) : null}
            {viewMode === 'year' ? (
              <CalendarYearView
                year={currentDate.getFullYear()}
                events={plottedEvents}
                onDayClick={(day) => {
                  setCurrentDate(startOfDay(day));
                  setViewMode('day');
                }}
              />
            ) : null}
          </div>
        </section>
      </div>

      <CalendarTasksModal
        open={tasksModalOpen}
        mode={tasksModalMode}
        initialDate={selectedDay}
        assignableUsers={tasksCalendarUsers}
        defaultOwnerUserId={defaultTaskOwnerUserId}
        viewingEvent={viewingTaskEvent}
        onClose={() => {
          setTasksModalOpen(false);
          setViewingTaskEvent(null);
          setDefaultTaskOwnerUserId(undefined);
        }}
        onSubmit={handleCreateTask}
        onUpdate={handleUpdateTask}
        onCancel={handleCancelTask}
        onExecutionChanged={loadTaskEvents}
      />

      <CalendarEventModal
        open={eventModalOpen}
        calendarName={activeCalendar?.name ?? 'Calendário'}
        initialDate={selectedDay}
        initialHour={selectedHour}
        editingEvent={editingEvent}
        onClose={closeEventModal}
        onSubmit={handleSaveEvent}
        onDelete={editingEvent ? handleDeleteEvent : undefined}
      />

      <NewCalendarModal
        open={newCalendarModalOpen}
        sourceCalendarId={
          activeCalendarId && activeCalendarId !== TASKS_CALENDAR_ID
            ? activeCalendarId
            : undefined
        }
        sourceCalendarName={
          activeCalendarId && activeCalendarId !== TASKS_CALENDAR_ID
            ? activeCalendar?.name
            : 'Calendário global'
        }
        onClose={() => setNewCalendarModalOpen(false)}
        onSubmit={handleCreateCalendar}
      />

      <CalendarWorkScheduleModal
        open={workScheduleModalOpen}
        calendar={activeCalendarDetails}
        onClose={() => setWorkScheduleModalOpen(false)}
        onSubmit={handleSaveWorkSchedule}
      />
    </PageShell>
  );
}
